import React, { useRef } from "react";
import AppSidebar from "./AppSidebar";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import BottomNav from "./BottomNav";
import { Outlet, useLocation } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import { useConversations } from "../../context/ConversationsContext";
import WizardChat from "../../components/profile/onboarding/WizardChat";

const SWIPE_THRESHOLD = 60;
const SWIPE_EDGE_ZONE = 30;

const MainLayout: React.FC = () => {
  const { isExpanded, isHovered, isMobileOpen, toggleMobileSidebar } = useSidebar();
  const { isChatSelected, isKeyboardOpen } = useConversations();
  const location = useLocation();
  const isConversationsPage = location.pathname === "/conversations";
  const hideHeader = isConversationsPage && isChatSelected;
  const hideBottomNav = isConversationsPage && (isChatSelected || isKeyboardOpen);

  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.innerWidth >= 1024) return;

    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    const isSwipeRight = deltaX > SWIPE_THRESHOLD;
    const isSwipeLeft = deltaX < -SWIPE_THRESHOLD;
    const startedFromEdge = touchStartX.current <= SWIPE_EDGE_ZONE;

    if (isSwipeRight && startedFromEdge && !isMobileOpen) {
      toggleMobileSidebar();
    } else if (isSwipeLeft && isMobileOpen) {
      toggleMobileSidebar();
    }
  };

  const mainContentMargin = isMobileOpen
    ? "ml-0"
    : isExpanded || isHovered
      ? "lg:ml-[290px]"
      : "lg:ml-[90px]";

  return (
    <div
      className="flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors duration-300 font-sans text-gray-900 dark:text-gray-100"
      style={{ height: "100dvh" }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <AppSidebar />
      <Backdrop />
      <div
        className={`flex flex-1 flex-col min-h-0 transition-all duration-300 ${mainContentMargin}`}
      >
        <AppHeader className={hideHeader ? "md:flex hidden" : undefined} />
        <main
          className={
            isConversationsPage
              ? "flex-1 min-h-0 w-full overflow-hidden p-0 m-0"
              : "mx-auto w-full max-w-screen-2xl p-4 md:p-6 2xl:p-10 lg:pt-0 pb-[72px] md:pb-0"
          }
        >
          <Outlet />
        </main>
      </div>
      {!hideBottomNav && <BottomNav />}
      <WizardChat />
    </div>
  );
};

export default MainLayout;
